import { Component, ViewChild, OnChanges, SimpleChanges, OnInit } from '@angular/core';
import { AgGridAngular, AgGridModule } from 'ag-grid-angular';
import { ReleaseMgmtService } from '@app/shared/services/releaseMgmtService';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { PageHeaderComponent } from '@app/shared/page-header/page-header.component';
import { CommonModule } from '@angular/common';
import { EnvServiceMappingComponent } from '@app/modules/env-service-mapping/env-service-mapping.component';
import moment from 'moment';
import { ICellRendererParams } from 'ag-grid-community';
import { LoaderService } from '@app/shared/services/loader.service';
// Define or import AuditTrailsDto interface
export interface AuditTrailsDto {
  releaseName: string;
  releaseType: string;
  environments: string;
  environmentServiceMappingJson: any;
  createdBy: string;
  createdDateTime: string;
  lastUpdatedBy: string;
  updatedDateTime: string;
}

@Component({
    standalone: true,
    selector: 'app-audit-trails',
    imports: [AgGridModule, MatIconModule, PageHeaderComponent, CommonModule, RouterModule],
    templateUrl: './audit-trails.component.html',
    styleUrl: './audit-trails.component.scss'
})
export class AuditTrailsComponent implements OnChanges, OnInit {
  @ViewChild('agGrid') agGrid: AgGridAngular;
  breadcrumb: any[] = [];

  releaseId = 0;
  paginationPageSize = 30;
  infiniteInitialRowCount = 30;
  styleChipDiv = 'width:100%; height: calc(100vh - 175px); flex: 1 1 auto; border: 1px solid #DFDFDF;';

  overlayNoRowsTemplate = `<span style="font-weight: 400; color: #757575; ">No records found</span>`;
  rowData: AuditTrailsDto[] = [];
  filteredRowData: AuditTrailsDto[] = [];
  pageTitle = 'Audit Trail';
  pageSubTitle = 'View the deployment status details for the selected release';
  columnDefs = [
    {
      headerName: 'Environments',
      field: 'environments',
      flex: 1,

      filter: true,
      tooltipField: 'environments',
      cellRenderer: params => {
        const value = params.value || '';
        if (!value.trim()) {
          return `<span>-</span>`;
        }
        const stages = value.split(',').map(s => s.trim()).filter(Boolean);
        let displayText = stages.slice(0, 2).join(', ');
        const tooltip = stages.join(', ');
        if (stages.length > 2) {
          displayText += ', ...';
          return `<span style="cursor: pointer;" title="${tooltip}">${displayText}</span>`;
        }
        return `<span style="cursor: ${stages.length ? 'pointer' : 'default'};" title="${tooltip}">${displayText}</span>`;
      }
    },
    {
      headerName: 'Applicable Services',
      field: 'applicableServices',

      flex: 1,
      filter: false, sortable: false,
      cellRenderer: params => {
        return `<a class="ag-link" style="color: #1976d2; font-weight: 400; text-decoration: underline; cursor: pointer;" title="Click to view details">View Details</a>`;
      },
      onCellClicked: (params) => {
        this.openDialog(params.data);
      }
    },
    {
      headerName: 'Created By',
      field: 'createdBy',

      flex: 1,
      tooltipField: 'createdBy'
    },
    {
      headerName: 'Created On',
      dataType: 'date',
      field: 'createdDateTime',
      filter: 'agDateColumnFilter',
      filterParams: {
        buttons: ['clear'],
        filterOptions: ['equals'],
        defaultOption: 'equals',
        suppressAndOrCondition: true,
        comparator: (filterLocalDateAtMidnight, cellValue) => {
          if (!cellValue) return -1;
          const cellDate = new Date(cellValue);
          if (
            cellDate.getFullYear() === filterLocalDateAtMidnight.getFullYear() &&
            cellDate.getMonth() === filterLocalDateAtMidnight.getMonth() &&
            cellDate.getDate() === filterLocalDateAtMidnight.getDate()
          ) {
            return 0;
          }
          return -1;
        }
      },
      tooltipValueGetter: (params: ICellRendererParams) => params.value ? moment(params.value).format('MMM DD, YYYY, HH:mm A') : '',
      cellRenderer: (params: ICellRendererParams) => {
        const formattedDate = params.value ? moment(params.value).format('MMM DD, YYYY, HH:mm A') : '';
        return formattedDate;
      }
    },
    {
      headerName: 'Last Updated By',
      field: 'lastUpdatedBy',
      flex: 1,
      tooltipField: 'lastUpdatedBy'
    },
    {
      headerName: 'Last Updated On',
      dataType: 'date',
      field: 'lastUpdatedDateTime',
      filter: 'agDateColumnFilter', // <-- Enable date picker filter
      filterParams: {
        buttons: ['clear'], 
        filterOptions: ['equals'],
        defaultOption: 'equals',
        suppressAndOrCondition: true,
        comparator: (filterLocalDateAtMidnight, cellValue) => {
          if (!cellValue) return -1;
          const cellDate = new Date(cellValue);
          if (
            cellDate.getFullYear() === filterLocalDateAtMidnight.getFullYear() &&
            cellDate.getMonth() === filterLocalDateAtMidnight.getMonth() &&
            cellDate.getDate() === filterLocalDateAtMidnight.getDate()
          ) {
            return 0;
          }
          return -1;
        }
      },
      tooltipValueGetter: (params: ICellRendererParams) => params.value ? moment(params.value).format('MMM DD, YYYY, HH:mm A') : '',
      cellRenderer: (params: ICellRendererParams) => {
        const formattedDate = params.value ? moment(params.value).format('MMM DD, YYYY, HH:mm A') : '';
        return formattedDate;
      }
    },
  ];

  defaultColDef = {
    minWidth: 0,
    with: 0,
    resizable: true,
    sortable: true,
    filter: true,
  };
  releaseTitle = '';
  ShowHealthStatus = false;
  isRefreshing: boolean;
  releaseSubtitle: string;

  constructor(public loader: LoaderService, private ReleaseMgmtService: ReleaseMgmtService, public route: ActivatedRoute, public dialog: MatDialog, public router: Router) { }
  ngOnInit() {
    this.route.params.subscribe(params => {
      if (params['releaseId']) {
        this.releaseId = params['releaseId'];
      }
    });
    const backTo = this.route.snapshot.queryParamMap.get('backTo');

    let backUrl = '/release/define-releases';

    if (backTo && this.releaseId) {
      backUrl = `/release/modify-${backTo}/${this.releaseId}`;
    }
    this.breadcrumb = [
      { label: 'Home', command: () => this.router.navigateByUrl('/release') },
      { label: 'Define ' + backTo, command: () => this.router.navigateByUrl(backUrl) },
      { label: 'Audit Trail', styleClass: 'active' }
    ];
  }
  openDialog(dta: AuditTrailsDto) {
    const dialogConfig = new MatDialogConfig(); //NOSONAR - Deprecation has been applied during migration. To remove warning, control has to be upgraded as per current version of Angular Library.
    dialogConfig.disableClose = true;
    dialogConfig.autoFocus = true;
    dialogConfig.height = '550px';
    dialogConfig.width = '800px';
    dialogConfig.data = {
      title: 'Applicable Services',
      popupcontent: dta.environmentServiceMappingJson
    };
    const dialogRef = this.dialog.open(EnvServiceMappingComponent, dialogConfig);
  }
  /* istanbul ignore next */
  onGridReady(params: any) {
    if (!params) return;
    this.agGrid.api = params.api;
    this.filterByServiceId();

    setTimeout(() => {
      params.api.sizeColumnsToFit();
    });
  }

  /* istanbul ignore next */
  refreshTable() {
    this.filterByServiceId();
  }


  ngOnChanges(changes: SimpleChanges) {
    this.filterByServiceId();

  }

  filterByServiceId() {
    this.isRefreshing = true;
    this.loader.showSpinner.next(true);

    this.ReleaseMgmtService.apiGetAuditTrailDetail(this.releaseId).subscribe({
      next: (response: AuditTrailsDto[]) => {
        this.rowData = response || [];
        this.filteredRowData = this.rowData;
        if (this.agGrid?.api) {
          this.releaseTitle = this.rowData.length > 0 ? this.rowData[0].releaseName : '';
          this.releaseSubtitle = this.rowData.length > 0 ? this.rowData[0].releaseType : '';
          this.agGrid.api.setGridOption('rowData', this.filteredRowData);
          this.agGrid.api.sizeColumnsToFit();
        }
        this.loader.showSpinner.next(false);

      },
      error: (error) => {
        console.error('Error fetching branch creation status:', error);
        this.rowData = [];
        this.filteredRowData = [];
        if (this.agGrid?.api) {
          this.agGrid.api.setGridOption('rowData', []);
          this.agGrid.api.sizeColumnsToFit();
        }
        this.loader.showSpinner.next(false);
      }
    });
    this.isRefreshing = false;

  }
}
